import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useToast } from '@/legacy/hooks';
import { useUserInfo } from '@/features/space-user';
import { paths } from '@/app/router/path';
import { resolveApiError } from '@/shared/api-error';
import { useDelegationAccessMutation } from '../../api/hooks/useDelegationAccessMutation';
import { useFakeAccess } from '../../hooks/useFakeAccess';

type SwitchAccessVars = {
	companyId: number;
	companyName: string;
	delegationsIds: number[];
};

/**
 * Routes on `companyChanged` alone: a company move via `useFakeAccess`, a same-company
 * delegation move via `useDelegationAccessMutation`. Both hand a target scope to
 * `transitionAuthScope` and neither names an endpoint — the role branch
 * (`isSuperUser || isStaff` → impersonation) is made in `acquireCredential`, which also sees
 * the active scope this seam does not, so a role check added here would only duplicate it.
 * That makes `useFakeAccess` a misnomer for an unprivileged caller: its company move is
 * issued against `/login/switch-credentials`. Each branch owns only its success copy.
 */
export const useCompanySwitchAccess = ({ onClose }: { onClose?: () => void }) => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const { data: userInfo } = useUserInfo();

	const { loginFakeAccess, isPending: isFakeAccessPending } = useFakeAccess({ onClose });

	const { mutate: switchDelegations, isPending: isDelegationsPending } =
		useDelegationAccessMutation({
			onSuccess: () => {
				showToast(t('auth.delegation_access_success'), 'success');
				onClose?.();
				navigate(paths.home());
			},
			onError: (error) => {
				showToast(resolveApiError(error).message.text, 'error');
			},
		});

	const switchAccess = ({ companyId, companyName, delegationsIds }: SwitchAccessVars) => {
		const companyChanged = companyId !== userInfo?.companyId;

		if (companyChanged) {
			loginFakeAccess({ companyId, companyName, delegationsIds, navigateOnSuccess: true });
			return;
		}

		switchDelegations({ delegations: delegationsIds });
	};

	return { switchAccess, isPending: isFakeAccessPending || isDelegationsPending };
};
